import React, { useState, useEffect } from 'react';
import { getTiposSocia, createTipoSocia, updateTipoSocia, deleteTipoSocia } from '../services/tiposSociaService';
import { FaUserTag, FaSearch, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import '../styles/TipoSociaManagement.css';
import '../styles/theme/toast.css';

const TipoSociaManagement = ({ createMode = false }) => {
    const [tiposSocia, setTiposSocia] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showForm, setShowForm] = useState(createMode);
    const [currentTipoSocia, setCurrentTipoSocia] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        observacion: ''
    });
    const [errors, setErrors] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);

    // Cargar tipos de socia
    useEffect(() => {
        fetchTiposSocia(page, pageSize);
    }, [page, pageSize]);

    

    // Consumir API real
    const fetchTiposSocia = async (p = 1, ps = 10) => {
        try {
            setLoading(true);
            const res = await getTiposSocia(p, ps);
            // API devuelve { data, total, page, pageSize, totalPages } cuando se usa paginación
            if (res.data && res.data.data !== undefined) {
                setTiposSocia(res.data.data || []);
                setTotal(res.data.total || 0);
                setPage(res.data.page || p);
                setPageSize(res.data.pageSize || ps);
                setTotalPages(res.data.totalPages || Math.ceil((res.data.total || 0) / (res.data.pageSize || ps)));
            } else {
                // Fallback: API devuelve lista completa
                setTiposSocia(res.data || []);
                setTotal(res.data ? res.data.length : 0);
                setTotalPages(1);
            }
        } catch (error) {
            console.error('Error al cargar tipos de socia:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Limpiar error cuando el usuario empiece a escribir
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
        if (!formData.observacion.trim()) newErrors.observacion = 'La observación es requerida';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        try {
            if (currentTipoSocia) {
                // Actualizar tipo de socia existente en API
                await updateTipoSocia(currentTipoSocia.TS, formData);
            } else {
                // Crear nuevo tipo de socia en API
                await createTipoSocia(formData);
            }
            await fetchTiposSocia();
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
            handleCloseModal();
        } catch (error) {
            console.error('Error al guardar tipo de socia:', error);
        }
    };


    const handleEdit = (tipoSocia) => {
        setCurrentTipoSocia(tipoSocia);
        setFormData({
            nombre: tipoSocia.TIPOSOCIA_NOMBRE || '',
            observacion: tipoSocia.TIPOSOCIA_OBSERVACION || ''
        });
        setShowForm(true);
    };


    const handleDelete = async (tipoSocia) => {
        if (window.confirm('¿Está seguro de eliminar este tipo de socia? Esta acción no se puede deshacer.')) {
            try {
                await deleteTipoSocia(tipoSocia.TS);
                await fetchTiposSocia();
            } catch (error) {
                console.error('Error al eliminar tipo de socia:', error);
            }
        }
    };

    const resetForm = () => {
        setFormData({
            nombre: '',
            observacion: ''
        });
        setCurrentTipoSocia(null);
        setErrors({});
    };

    const handleCloseModal = () => {
        setShowForm(false);
        resetForm();
    };

    const filteredTiposSocia = tiposSocia.filter(tipoSocia =>
        (tipoSocia.TIPOSOCIA_NOMBRE?.toLowerCase() || '').includes((searchTerm || '').toLowerCase()) ||
        (tipoSocia.TIPOSOCIA_OBSERVACION?.toLowerCase() || '').includes((searchTerm || '').toLowerCase()) ||
        (tipoSocia.TS?.toString() || '').includes((searchTerm || '').toLowerCase())
    );

    return (
        <div className="tipo-socia-management-container">
            {showToast && (
                <div className="custom-toast custom-toast-success">
                    ¡Tipo de socia guardado con éxito!
                </div>
            )}
            <div className="tipo-socia-management-header">
                <h1>Gestión de Tipos de Socia</h1>
                <p>Administre los tipos de socias del sistema</p>
            </div>

            {!showForm ? (
                <>
                    <div className="tipo-socia-management-actions">
                        <div className="search-section">
                            <input
                                type="text"
                                placeholder="Buscar tipo de socia..."
                                className="search-input"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <FaSearch className="search-icon" />
                        </div>
                        <button 
                            className="btn-primary register-btn"
                            onClick={() => setShowForm(true)}
                        >
                            <FaPlus /> Nuevo Tipo de Socia
                        </button>
                    </div>

                    <div className="tipo-socia-table-container">
                        <table className="tipo-socia-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Nombre</th>
                                    <th>Observación</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTiposSocia.map(tipoSocia => (
                                    <tr key={tipoSocia.TS}>
                                        <td><span className="tipo-socia-id">{tipoSocia.TS}</span></td>
                                        <td>
                                            <div className="tipo-socia-name">
                                                <div className="tipo-socia-icon">
                                                    <FaUserTag />
                                                </div>
                                                {tipoSocia.TIPOSOCIA_NOMBRE}
                                            </div>
                                        </td>
                                        <td>{tipoSocia.TIPOSOCIA_OBSERVACION}</td>
                                        <td>
                                            <div className="actions">
                                                <button
                                                    className="action-btn edit-btn"
                                                    onClick={() => handleEdit(tipoSocia)}
                                                    title="Editar"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    className="action-btn delete-btn"
                                                    onClick={() => handleDelete(tipoSocia)}
                                                    title="Eliminar"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredTiposSocia.length === 0 && (
                            <div className="table-footer">
                                No se encontraron tipos de socia
                            </div>
                        )}
                    </div>
                    
                    <div className="records-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                        {loading ? (
                            <div>Cargando...</div>
                        ) : (
                            <>
                                <div>
                                    Mostrando <strong>{filteredTiposSocia.length}</strong> de <strong>{total}</strong> tipos de socia
                                </div>
                                <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <button className="btn-secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Anterior</button>
                                    <span> Página <strong>{page}</strong> de <strong>{totalPages || 1}</strong> </span>
                                    <button className="btn-secondary" onClick={() => setPage(p => Math.min((totalPages || 1), p + 1))} disabled={page === (totalPages || 1)}>Siguiente</button>
                                    <label style={{ marginLeft: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span>Por página:</span>
                                        <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                                            <option value={5}>5</option>
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                        </select>
                                    </label>
                                </div>
                            </>
                        )}
                    </div>
                </>
            ) : null}
            
            {showForm && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                <FaUserTag />
                                {currentTipoSocia ? 'Editar Tipo de Socia' : 'Nuevo Tipo de Socia'}
                            </h3>
                            <button className="modal-close" onClick={handleCloseModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleSubmit} className="tipo-socia-form">
                                <div className="form-section">
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="nombre">Tipo de Socia</label>
                                <input
                                    type="text"
                                    id="nombre"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleInputChange}
                                    className={errors.nombre ? 'error' : ''}
                                    placeholder="Ingrese el nombre del tipo de socia..."
                                />
                                {errors.nombre && (
                                    <span className="error-message">{errors.nombre}</span>
                                )}
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label htmlFor="observacion">Observación</label>
                                <textarea
                                    id="observacion"
                                    name="observacion"
                                    value={formData.observacion}
                                    onChange={handleInputChange}
                                    className={errors.observacion ? 'error' : ''}
                                    placeholder="Descripción detallada del tipo de socia..."
                                    rows="4"
                                />
                                {errors.observacion && (
                                    <span className="error-message">{errors.observacion}</span>
                                )}
                            </div>
                        </div>
                    </div>

                                <div className="form-actions">
                                    <button
                                        type="button"
                                        className="btn-secondary"
                                        onClick={handleCloseModal}
                                    >
                                        Cancelar
                                    </button>
                                    <button type="submit" className="btn-primary">
                                        {currentTipoSocia ? 'Guardar Cambios' : 'Crear Tipo de Socia'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TipoSociaManagement;